import { useLocalization, useGoBack } from '@magicdoorinc/ui';
import { useLocation } from '@solidjs/router';
import { createSignal, createMemo, createEffect, Show } from 'solid-js';
import Breadcrumb from '~/components/common/Breadcrumb';
import { useMenu } from '~/components/layouts/Menu/MenuContext';
import {
  useOwners,
  useProperties,
  usePropertyManagers,
  useRentalApplication,
  useTenants,
  useUnitListings,
  useUnits,
  useVendors,
} from '~/contexts/global';
import usePresenter from '~/framework/hooks/usePresenter';
import { TitlePresenter } from '~/pdfsigner/presenters/TitlePresenter';
import { useFunctionalBreadcrumbs } from './functional/hooks';
import { useResponsiveBreadcrumbs } from './hooks/useResponsiveBreadcrumbs';
import type { BreadcrumbContext } from './functional/types';

const BreadcrumbContainer = (props: { isSubNavigation?: boolean; mobileVariant?: 'rootOnly' | 'currentOnly' }) => {
  const { t } = useLocalization();
  const location = useLocation();
  const goBack = useGoBack();
  const menu = useMenu();

  const { current: currentProperty } = useProperties();
  const { unitListingDetail } = useUnitListings();
  const { applicationStore } = useRentalApplication();
  const { unit } = useUnits();
  const { current: currentTenant } = useTenants();
  const { current: currentOwner } = useOwners();
  const { current: currentVendor } = useVendors();
  const { currentManager: currentPropertyManager } = usePropertyManagers();
  const { model: presentableTitle } = usePresenter(TitlePresenter);

  const [containerRef, setContainerRef] = createSignal<HTMLDivElement | null>(null);

  const context = createMemo(
    (): BreadcrumbContext => ({
      t,
      path: location.pathname.toLowerCase(),
      isMobile: menu?.isMobile() || false,
      isLoaded: !!currentProperty()?.portfolio.name,
      goBack,

      currentProperty,
      unitListingDetail,
      applicationStore,
      unit,
      currentTenant,
      currentOwner,
      currentVendor,
      currentPropertyManager,
      presentableTitle,
    })
  );

  const { breadcrumbItems, backLink } = useFunctionalBreadcrumbs(() => context());

  createEffect(() => {
    context();
    breadcrumbItems();
    backLink();
  });

  const isSub = createMemo(() => props.isSubNavigation);

  const { visibleBreadcrumbs } = useResponsiveBreadcrumbs(
    () => breadcrumbItems(),
    containerRef,
    () => location.pathname.toLowerCase(),
    () => props.mobileVariant || (isSub() ? 'currentOnly' : 'rootOnly')
  );

  const shouldRender = createMemo(() => {
    const items = breadcrumbItems();
    if (items.length === 0) return false;
    if (!isSub()) return true;
    if (items.length <= 1) return false;
    if (items.length >= 3) return true;
    const first = items[0];
    return !!first.link && first.link !== '';
  });

  return (
    <Show when={shouldRender()}>
      <div ref={setContainerRef} class="w-full" data-breadcrumb-path={context().path}>
        <Breadcrumb
          backLink={isSub() ? backLink() : menu?.isMobile() ? undefined : backLink()}
          items={visibleBreadcrumbs()}
          idPrefix={isSub() ? 'breadcrumb-sub' : 'breadcrumb'}
        />
      </div>
    </Show>
  );
};

export default BreadcrumbContainer;
